import { CANVAS_INSERT_PARAGRAPHS_MAX, type CanvasActionInput, type CanvasSelector } from '@hector/shared-types';
import { lireLesMarques } from './marquesDeTexte';
import {
  isOpenParagraphInsertionRevision,
  type AgentTrackedInsertion,
} from './agentTrackedInsertionLedger';
import {
  abortedBeforeMutation,
  buildVerifiedReceipt,
  inconclusiveAfterMutation,
  partialAfterMutation,
  toCanvasSnapshotSummary,
  type CanvasActionReceipt,
  type ResolvedActionTarget,
  type VerificationCheck,
} from './actionReceipt';
import { estArretDeLot, type PeutEtreUnePromesse } from './executionAsynchrone';
import { muterSousFences, revisionCourante, type RevisionDeDocument } from './fencesDeMutation';
import {
  familleDuDocument,
  idsDeRevision,
  operationDuDocument,
  readCanvasSnapshot,
  readCompleteTrackedChangesAtDocumentRevision,
  type CanvasSnapshot,
  type CanvasSnapshotBlock,
} from './canvasSnapshot';
import {
  captureEffectiveBlockStyle,
  captureEffectiveStyleFromSdmNode,
  effectiveStyleFingerprint,
  fusionnerStylesEffectifs,
  facettesDeParagrapheSeules,
  facettesDePoliceNonObservees,
  formattingMatchedObservable,
  hasObservableEffectiveStyle,
  selectDefaultInsertionStyleSource,
  type EffectiveBlockStyle,
  type FacetteDePoliceNonObservee,
} from './effectiveStyle';
import { messageSelecteurIntrouvable, messageTargetNotInFinalInspection, resolveCanvasSelector } from './selectorResolver';
import { modeDeRevision, type CanvasActionContext } from './textActionExecutors';
import { signalerLAvancee } from './avanceeParLots';

type StyleActionInput = Extract<CanvasActionInput, { action: 'insert_paragraphs' | 'apply_style' }>;
type CanvasPlacement = Extract<CanvasActionInput, { action: 'insert_paragraphs' }>['placement'];
type ProviderMethod = (input?: unknown, options?: unknown) => unknown;
const PAGE_SIZE = 250;
/** OOXML fixed ratio: blocks.list reports indentation in twips, SDParagraphProps wants points. */
const TWIPS_PER_POINT = 20;

/**
 * SuperDoc ≥1.44 splits block styling across three surfaces (verified against
 * superdoc 1.44.1 dist typings + operation catalog, 2026-07-13):
 * - `format.apply({target|ref, inline})` — inline run patch only; the pre-1.44
 *   `paragraph` field now throws `Unknown field "paragraph" on format.apply input`.
 * - `format.paragraph.setAlignment/setIndentation/setNumbering` — direct paragraph
 *   facets, addressed by block node, indentation in twips (integers).
 * - `styles.paragraph.setStyle` — named paragraph style reference.
 */
interface StyleSurfaces {
  create?: { paragraph?: ProviderMethod };
  format?: {
    apply?: ProviderMethod;
    paragraph?: {
      setAlignment?: ProviderMethod;
      setIndentation?: ProviderMethod;
      setNumbering?: ProviderMethod;
      setSpacing?: ProviderMethod;
      /**
       * Sauts et enchaînement de paragraphe. Employée ici pour UNE chose : ÔTER
       * le `pageBreakBefore` qui peut être hérité par un bloc neuf (#998).
       * Poser un saut voulu reste le travail d'`insert_page_break`.
       */
      setFlowOptions?: ProviderMethod;
    };
  };
  styles?: { paragraph?: { setStyle?: ProviderMethod } };
}

interface InsertCapableContext extends CanvasActionContext {
  doc: CanvasActionContext['doc'] & StyleSurfaces;
}

interface StyleStepOutcome {
  op: string;
  ok: boolean;
  detail?: string;
}

function isRecord(input: unknown): input is Record<string, unknown> {
  return typeof input === 'object' && input !== null && !Array.isArray(input);
}

function items(input: unknown): Record<string, unknown>[] {
  if (Array.isArray(input)) return input.filter(isRecord);
  if (!isRecord(input)) return [];
  const values = Array.isArray(input.blocks) ? input.blocks : Array.isArray(input.items) ? input.items : [];
  return values.filter(isRecord);
}

function nodeIdFromBlock(block: Record<string, unknown>): string | undefined {
  const value = block.nodeId ?? block.blockId ?? block.id;
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

function textFromBlock(block: Record<string, unknown>): string {
  return typeof block.text === 'string'
    ? block.text
    : typeof block.textPreview === 'string' ? block.textPreview : '';
}

function snapshot(ctx: CanvasActionContext, window = { offset: 0, limit: 80 }): Promise<CanvasSnapshot> {
  return readCanvasSnapshot(ctx.doc, window);
}

async function scanRawBlocks(ctx: CanvasActionContext): Promise<{ ok: true; blocks: Record<string, unknown>[] } | { ok: false; code: string }> {
  const blocks: Record<string, unknown>[] = [];
  for (let offset = 0; ; offset += PAGE_SIZE) {
    const result = await ctx.doc.blocks?.list?.({ offset, limit: PAGE_SIZE, includeText: true });
    if (isRecord(result) && result.success === false) return { ok: false, code: 'STYLE_BLOCKS_READ_FAILED' };
    const page = items(result);
    blocks.push(...page);
    if (page.length < PAGE_SIZE) return { ok: true, blocks };
  }
}

/** Derives the selection-friendly view from the raw scan so the document is read once, not twice. */
function toSelectionBlocks(rawBlocks: readonly Record<string, unknown>[]): CanvasSnapshotBlock[] {
  return rawBlocks.flatMap((raw, index) => {
    const nodeId = nodeIdFromBlock(raw);
    if (nodeId === undefined) return [];
    const fullText = textFromBlock(raw);
    // `headingLevel` sert au choix de la source de style par défaut : un titre
    // porté par un `paragraph` stylé ne doit pas passer pour du corps de texte.
    const headingLevel = typeof raw.headingLevel === 'number' ? raw.headingLevel : undefined;
    return [{
      nodeId,
      ordinal: index + 1,
      nodeType: typeof raw.nodeType === 'string' ? raw.nodeType : 'paragraph',
      fullText,
      text: fullText.slice(0, 2_000),
      ...(headingLevel === undefined ? {} : { headingLevel }),
    }];
  });
}

function targetSummary(target: CanvasSnapshotBlock, candidateCount?: number): ResolvedActionTarget {
  return { nodeId: target.nodeId, ordinal: target.ordinal, text: target.text.slice(0, 500), ...(candidateCount === undefined ? {} : { candidateCount }) };
}

function rawBlockByNodeId(rawBlocks: readonly Record<string, unknown>[], nodeId: string): Record<string, unknown> | undefined {
  return rawBlocks.find((block) => nodeIdFromBlock(block) === nodeId);
}

/** Le document API vu comme surface de LECTURE par bloc (#991). */
interface ReadSurfaces {
  getNodeById?: ProviderMethod;
}

/**
 * Une lecture de style par bloc, avec **son propre verdict de lisibilité**.
 *
 * `lu` ne dit pas « il y a du style », il dit « la lecture a abouti ». La
 * distinction porte tout le ticket : un bloc dont on a LU le nœud et qui ne
 * porte aucune propriété est au style par défaut du document — fait positif,
 * sur lequel on peut agir. Un bloc dont la lecture a ÉCHOUÉ ne dit rien du
 * tout, et c'est le seul cas où le refus reste justifié. Les deux rendaient
 * jusqu'ici le même `{}`, indiscernables.
 */
interface LectureDeStyle {
  style: EffectiveBlockStyle;
  lu: boolean;
  /** Ce qui a empêché de conclure, pour le message de refus. */
  echec?: string;
}

/**
 * Le style effectif d'un bloc, réuni des DEUX surfaces de lecture par bloc.
 *
 * `blocks.list` ne publie plus que `styleId` en 2.6.0 ; `getNodeById` publie
 * l'alignement, le retrait et l'espacement (relevé du 2026-08-16, en tête de
 * `effectiveStyle.ts`). Lire les deux n'est pas une ceinture-bretelles : c'est
 * la seule façon de couvrir à la fois les documents à styles nommés et les
 * ~80 % de documents réels qui n'en portent aucun.
 *
 * ⚠️ `getNodeById` est lue en BEST-EFFORT, jamais exigée : elle n'est pas
 * déclarée dans les `requiredOperations` du verbe, donc son absence ne doit pas
 * couper le pinceau — elle le ramène à ce que `blocks.list` sait dire. Une
 * garde qui ne sait pas conclure avertit, elle ne bloque pas.
 *
 * ## ⚠️ NE PAS « optimiser » ce coût — la question a été tranchée sur mesure
 *
 * Cette fonction est appelée **1 + 2N fois** par `insert_paragraphs` (1 pour le
 * bloc source, N pour la relecture des blocs insérés, N de plus si une
 * réparation de style a lieu). C'est structurel : la relecture de vérification
 * DOIT emprunter la même surface que la capture, sinon l'écart est permanent et
 * déclenche une réparation de style après chaque insertion réussie.
 *
 * L'optimisation évidente — ne lire richement les cibles que si la source porte
 * une facette publiée seulement par le nœud SDM — a été **explicitement
 * écartée** (arbitrage du 2026-08-16). Elle rendrait la vérification plus pauvre
 * que l'écriture : un bloc inséré portant une facette absente de la source
 * passerait pour conforme. C'est exactement le mécanisme du défaut que #991
 * vient de fermer — un receipt vert sur un paragraphe qui sortait à gauche et
 * sans retrait. Le reconstruire à l'endroit qu'on vient de réparer serait un
 * mauvais échange, d'autant qu'il n'achèterait rien :
 *
 * **Pire cas CONTRACTUEL mesuré** (`tests/ws-mocked/mesure-cout-lecture-par-bloc.spec.ts`,
 * 2026-08-16) — N n'est pas borné par l'usage mais par le schéma, qui accepte
 * jusqu'à 80 paragraphes (`InsertParagraphsInputSchema.texts.max(80)`). Sur une
 * insertion de 80 : **81 appels, 1 ms cumulée, 0,007 ms par appel — 0 % d'une
 * action de 858 ms.** La branche de réparation n'a pas été exercée ; au tarif
 * relevé, les 80 appels supplémentaires coûteraient ~0,6 ms. Il n'y a pas de
 * problème de performance à résoudre ici, et la mesure est à refaire avant de
 * conclure autrement.
 */
async function lireStyleEffectifDuBloc(
  ctx: CanvasActionContext,
  rawBlock: Record<string, unknown>,
): Promise<LectureDeStyle> {
  const depuisListe = captureEffectiveBlockStyle(rawBlock);
  const nodeId = nodeIdFromBlock(rawBlock);
  const getNodeById = (ctx.doc as CanvasActionContext['doc'] & ReadSurfaces).getNodeById;
  if (nodeId === undefined) {
    return { style: depuisListe, lu: false, echec: 'the listed block carries no nodeId to address a per-block read with' };
  }
  if (typeof getNodeById !== 'function') {
    // La lecture par bloc du modèle documentaire manque : on ne sait pas
    // distinguer « bloc nu » de « bloc illisible ». `blocks.list` reste seule
    // juge, et elle ne publie que `styleId`.
    return {
      style: depuisListe,
      lu: hasObservableEffectiveStyle(depuisListe),
      echec: 'editor.doc.getNodeById is unavailable, so paragraph properties (alignment, indentation, spacing) cannot be read on this editor',
    };
  }
  try {
    const nodeType = typeof rawBlock.nodeType === 'string' ? rawBlock.nodeType : 'paragraph';
    const noeud = await getNodeById({ nodeId, nodeType });
    return { style: fusionnerStylesEffectifs(depuisListe, captureEffectiveStyleFromSdmNode(noeud)), lu: true };
  } catch (error) {
    if (estArretDeLot(error)) throw error;
    return {
      style: depuisListe,
      lu: hasObservableEffectiveStyle(depuisListe),
      echec: `editor.doc.getNodeById(nodeId=${nodeId}) threw: ${error instanceof Error ? error.message.slice(0, 160) : String(error).slice(0, 160)}`,
    };
  }
}

async function resolveTarget(ctx: CanvasActionContext, selector: CanvasSelector, intent: StyleActionInput['action']): Promise<{ ok: true; target: CanvasSnapshotBlock; candidateCount: number } | { ok: false; code: string; message: string }> {
  const result = await resolveCanvasSelector(ctx.doc, selector, {
    requireUnique: true,
    inspectedPublicNodeIds: ctx.inspectedPublicNodeIds,
  });
  if (!result.ok) {
    return {
      ok: false,
      code: result.code,
      message: result.code === 'TARGET_NOT_IN_FINAL_INSPECTION'
        ? messageTargetNotInFinalInspection()
        : result.code === 'SELECTOR_NOT_FOUND'
          ? messageSelecteurIntrouvable(`canvas_perform_action ${intent}`, selector, result.candidateCount)
          : `canvas_perform_action ${intent} could not resolve the requested selector before any write; re-run canvas_inspect before retrying.`,
    };
  }
  return result;
}

// Les révisions suivies (et leur `blockId` d'ancrage) sont lues par le snapshot
// et portées par lui — `canvasSnapshot.lireLesRevisionsSuivies` (#994). Ce
// module en avait sa propre copie, la TROISIÈME du dépôt : elle rappelait
// `trackChanges.list()` juste après une lecture qui venait déjà de le faire.

function compactFragment<T extends Record<string, unknown>>(input: T): Partial<T> | undefined {
  const entries = Object.entries(input).filter(([, value]) => value !== undefined);
  return entries.length === 0 ? undefined : Object.fromEntries(entries) as Partial<T>;
}

/** InlineRunPatch facet of a captured style (fontSize in points, color as plain string). */
function inlinePatchFor(style: EffectiveBlockStyle): Record<string, unknown> | undefined {
  return compactFragment({
    fontFamily: style.fontFamily,
    fontSize: style.fontSizePt,
    color: style.color,
    bold: style.bold,
    underline: style.underline,
  });
}

/**
 * LA SORTIE PRATICABLE, à joindre à tout refus de source de style (#991).
 *
 * Un refus qui renvoie l'agent inspecter le document lui fait refaire un tour
 * pour rien : la ré-inspection rendra exactement le même document illisible, et
 * le verbe refusera à l'identique. C'est ce cycle qui a coûté six échecs et
 * trois minutes dans la conversation autopsiée, avant que l'agent ne conclue
 * qu'il fallait purger le document de l'avocat.
 *
 * Un message d'erreur est lu par un modèle, hors contexte : il doit nommer le
 * geste suivant, pas la cause seule.
 */
const SORTIE_SANS_SOURCE_DE_STYLE =
  'Practicable exit: retry the SAME call WITHOUT any style source — the paragraphs then take the document default style, which is what a document with no named styles applies anyway. Use apply_style with an explicit styleId afterwards if a specific named style is required.';

/**
 * La même sortie, en champs MACHINE.
 *
 * Le message et `recovery.action` doivent dire la même chose : un message qui
 * conseille de réessayer pendant que le champ structuré ordonne `reinspect`
 * laisse l'agent choisir — et il a choisi la ré-inspection, six fois de suite.
 */
const NEXT_ACTION_SANS_SOURCE_DE_STYLE = 'Retry the same call without styleSource.';
const RECOVERY_SANS_SOURCE_DE_STYLE = {
  action: 'retry' as const,
  reason: 'Nothing was written. Retrying WITHOUT a style source succeeds — re-inspecting would return the same unreadable source block.',
};

/** Provider surfaces the captured style facets require, in "namespace.method" form. */
function requiredStyleSurfaces(style: EffectiveBlockStyle): string[] {
  return [
    ...(style.paragraphStyleId === undefined ? [] : ['styles.paragraph.setStyle']),
    ...(style.alignment === undefined ? [] : ['format.paragraph.setAlignment']),
    ...(style.indentation === undefined ? [] : ['format.paragraph.setIndentation']),
    ...(style.spacing === undefined ? [] : ['format.paragraph.setSpacing']),
    ...(style.numbering?.numId === undefined ? [] : ['format.paragraph.setNumbering']),
    ...(inlinePatchFor(style) === undefined ? [] : ['format.apply']),
  ];
}

/**
 * L'opération d'une surface, LIÉE à la famille qui la porte.
 *
 * ⚠️ Liée, jamais extraite : `poserUneFacette` l'appelle nue, et une opération du fournisseur lit son receveur.
 * Détachée, elle lève et chaque facette se dit refusée (revue de la PR #2257).
 */
function surfaceMethod(doc: StyleSurfaces, surface: string): ProviderMethod | undefined {
  const deParagraphe = (famille: 'styles' | 'format', operation: string) =>
    operationDuDocument(familleDuDocument(doc, famille), 'paragraph', operation);
  switch (surface) {
    case 'styles.paragraph.setStyle': return deParagraphe('styles', 'setStyle');
    case 'format.paragraph.setAlignment': return deParagraphe('format', 'setAlignment');
    case 'format.paragraph.setIndentation': return deParagraphe('format', 'setIndentation');
    case 'format.paragraph.setNumbering': return deParagraphe('format', 'setNumbering');
    case 'format.paragraph.setSpacing': return deParagraphe('format', 'setSpacing');
    case 'format.paragraph.setFlowOptions': return deParagraphe('format', 'setFlowOptions');
    case 'format.apply': return operationDuDocument(doc, 'format', 'apply');
    default: return undefined;
  }
}

function missingStyleSurfaces(doc: StyleSurfaces, style: EffectiveBlockStyle): string[] {
  return requiredStyleSurfaces(style).filter((surface) => typeof surfaceMethod(doc, surface) !== 'function');
}

/** NO_OP means "already in the requested state" — the effective_style re-read stays the judge. */
async function stepOutcome(op: string, run: () => PeutEtreUnePromesse<unknown>): Promise<StyleStepOutcome> {
  try {
    const result = await run();
    if (isRecord(result) && result.success === false) {
      const failure = isRecord(result.failure) ? result.failure : {};
      const code = typeof failure.code === 'string' ? failure.code : 'provider_failure';
      if (code === 'NO_OP') return { op, ok: true, detail: 'NO_OP (already applied)' };
      const message = typeof failure.message === 'string' ? `: ${failure.message.slice(0, 160)}` : '';
      return { op, ok: false, detail: `${code}${message}` };
    }
    return { op, ok: true };
  } catch (error) {
    // Un arrêt de lot traverse : ce `catch` existe pour qu'une facette cassée
    // n'emporte pas les autres, pas pour continuer à écrire dans un document qui
    // a changé sous l'action.
    if (estArretDeLot(error)) throw error;
    return { op, ok: false, detail: error instanceof Error ? error.message.slice(0, 200) : String(error).slice(0, 200) };
  }
}

/** Fresh block ref straight from the provider — earlier mutations may invalidate any cached one. */
async function freshBlockRef(ctx: CanvasActionContext, nodeId: string): Promise<string | undefined> {
  const scan = await scanRawBlocks(ctx);
  if (!scan.ok) return undefined;
  const ref = rawBlockByNodeId(scan.blocks, nodeId)?.ref;
  return typeof ref === 'string' && ref.length > 0 ? ref : undefined;
}

/**
 * Pose UNE facette sur UN bloc, par une surface nommée du Document API.
 *
 * UNE opération mutante = UNE fenêtre de mutation avec son mode de révision
 * (#939, garde (d)). Chaque facette relit la révision attendue juste avant : ces
 * surfaces de réparation n'en portaient AUCUNE, alors qu'elles écrivent (#939,
 * garde (b)).
 *
 * ⚠️ TOUJOURS la révision RELUE, jamais `ctx.expectedRevision`. Celle de
 * l'enveloppe décrit le document tel qu'il était à l'entrée de l'ACTION, et tout
 * appelant d'ici écrit APRÈS une insertion qui l'a déjà fait avancer : la poser
 * ferait refuser la pose en `STALE_REVISION` sur une insertion qui, elle, a bel
 * et bien eu lieu — le modèle réinspecterait puis ré-insérerait, dupliquant des
 * paragraphes dans l'acte de l'avocat. `ctx.expectedRevision` n'est légitime que
 * pour la PREMIÈRE mutation d'une action, ce qu'aucun appel d'ici n'est.
 * Corollaire : plus besoin de deviner « est-ce la première ? » à partir du
 * nombre de résultats déjà collectés, un compte qui incluait aussi les échecs
 * NON mutants (surface absente, ref introuvable).
 *
 * Deux appelants, et c'est pour eux que cette fonction a été sortie de la
 * réparation de style : la copie de style (`applyEffectiveStyleToBlock`) et le
 * retrait du saut hérité (`retirerLeSautHerite`). Les laisser chacun rouvrir sa
 * fenêtre de mutation ferait diverger la règle de révision ci-dessus au premier
 * correctif.
 */
async function poserUneFacette(
  ctx: CanvasActionContext,
  doc: StyleSurfaces,
  surface: string,
  nodeId: string,
  input: Record<string, unknown>,
  revisionDeRepli: RevisionDeDocument,
): Promise<StyleStepOutcome> {
  const method = surfaceMethod(doc, surface);
  if (typeof method !== 'function') return { op: surface, ok: false, detail: 'surface unavailable on this editor' };
  const expectedRevision = await revisionCourante(ctx, revisionDeRepli);
  ctx.verifierEpoque(`style:${surface}:${nodeId}`);
  return stepOutcome(surface, () => ctx.mutate(
    () => method(input, { changeMode: 'direct' as const, expectedRevision }),
    { changeMode: 'direct', expectedRevision },
  ));
}

/**
 * Retire le SAUT DE PAGE HÉRITÉ des paragraphes que l'insertion vient de créer.
 *
 * ## Le défaut, mesuré et documenté ([#998])
 *
 * `doc.insert` fait HÉRITER au bloc neuf le `pageBreakBefore` de son voisin :
 * `word/document.xml` porte DEUX `<w:pageBreakBefore/>` alors qu'un seul a été
 * posé. Et le peintre ne rend PAS un paragraphe qui ouvre lui-même une page en
 * fin de document — la page vide EST cette page. L'agent écrit, le receipt est
 * vert, l'écran de l'avocat ne montre rien.
 *
 * Dans un acte Hector, `PLAISE AU TRIBUNAL`, `BORDEREAU` et `PIÈCES JOINTES`
 * portent tous ce saut (`MARKER_RULES` du parseur structurel partagé) — donc
 * précisément les voisins de « ajoute la pièce n°12 » ou « réécris le
 * bordereau ». Le saut se retransmet ensuite d'un paragraphe inséré au suivant :
 * un bordereau de huit pièces rendait huit pages.
 *
 * ## Pourquoi ce retrait vit ICI, et pourquoi il ne pouvait pas être ailleurs
 *
 * Le même héritage avait été refermé sur les deux autres chemins d'écriture — le
 * pont legacy (`state/canvasBridge.ts`, `retirerLeSautHerite`) et la génération
 * d'acte (`editor/canvasStructuralNormalization.ts`). Le canal vivant de l'agent
 * n'en avait pas : il n'était pas encore le chemin de production. Il l'est
 * devenu quand le pont legacy a perdu son émetteur (#1338), et le défaut est
 * revenu par là — relevé en recette le 2026-09-12 sur un bordereau réécrit.
 *
 * ## Ce que ce retrait n'est PAS
 *
 * Il ne décide d'aucune mise en page : il remet le paragraphe neuf dans l'état
 * où l'agent l'a demandé, c'est-à-dire sans saut. Un saut VOULU se pose après
 * l'écriture, par `insert_page_break`, et la description de ce verbe dit déjà
 * qu'il se pose en dernier — poser le saut avant d'écrire la suite condamnerait
 * cette suite à l'invisibilité.
 *
 * ⚠️ **Aucune relecture ne peut confirmer ce retrait** : `pageBreakBefore` sort
 * toujours `false` de la projection SDM en 2.6.0 (#986), donc une postcondition
 * bâtie dessus serait verte même sans correctif. La preuve d'effet est l'accusé
 * du fournisseur ici, et la PAGINATION PEINTE dans le navigateur —
 * `tests/ws-mocked/canvas-bordereau-saut-non-herite.spec.ts`, vérifiée rouge sur
 * le code d'avant (2 pages → 5, et les trois pièces peintes nulle part).
 */
async function retirerLeSautHerite(
  ctx: CanvasActionContext,
  doc: StyleSurfaces,
  blocs: readonly { nodeId: string; nodeType: string }[],
  revisionDeRepli: RevisionDeDocument,
): Promise<StyleStepOutcome[]> {
  // ⚠️ **Surface absente = on ne tente RIEN, et le receipt ne promet rien.**
  //
  // Le retrait est un REMÈDE, pas la demande de l'avocat : faire échouer la
  // vérification d'une insertion qui a bel et bien eu lieu pousserait le modèle
  // à la rejouer — donc à dupliquer des paragraphes — pour un éditeur dont on
  // ne sait même pas s'il fait hériter le saut. Les deux autres chemins
  // d'écriture traitent cette surface de la même façon : au mieux
  // (`essayer(...)` dans `editor/canvasStructuralNormalization.ts`, garde de
  // fonction dans `state/canvasBridge.ts`).
  //
  // La disparition de la surface n'est pas laissée sans garde pour autant :
  // `superdocCapabilityContract.integration.test.ts` confronte le catalogue
  // installé au snapshot versionné, et la garde navigateur du bordereau verrait
  // le défaut revenir à l'écran.
  if (typeof doc.format?.paragraph?.setFlowOptions !== 'function') {
    // eslint-disable-next-line no-console -- une absence de surface doit se voir dans la console de l'avocat, pas seulement dans un receipt.
    console.warn(
      '[canvas-agent] insert_paragraphs : `format.paragraph.setFlowOptions` absente de cet éditeur — le saut de page '
      + 'HÉRITÉ des paragraphes insérés n\'a pas pu être retiré. Ils sont dans le document mais peuvent rester '
      + 'invisibles à l\'écran (#998).',
    );
    return [];
  }
  const outcomes: StyleStepOutcome[] = [];
  for (const bloc of blocs) {
    outcomes.push(await poserUneFacette(
      ctx,
      doc,
      'format.paragraph.setFlowOptions',
      bloc.nodeId,
      { target: { kind: 'block', nodeType: bloc.nodeType, nodeId: bloc.nodeId }, pageBreakBefore: false },
      revisionDeRepli,
    ));
  }
  return outcomes;
}

/**
 * Applies every captured style facet to one block through the 1.44 surfaces, one
 * catch per step: a broken facet must surface in the receipt (via
 * `style_provider_calls`) without aborting the remaining facets — the pre-fix
 * behavior threw on the first step and produced an undiagnosable
 * `document_api_error` while the mutation was already half-applied.
 */
async function applyEffectiveStyleToBlock(
  ctx: CanvasActionContext,
  doc: StyleSurfaces,
  block: { nodeId: string; nodeType: string },
  style: EffectiveBlockStyle,
  revisionDeRepli: RevisionDeDocument,
): Promise<StyleStepOutcome[]> {
  const outcomes: StyleStepOutcome[] = [];
  const paragraphTarget = { kind: 'block', nodeType: block.nodeType, nodeId: block.nodeId };
  const runSurface = async (surface: string, input: Record<string, unknown>): Promise<void> => {
    outcomes.push(await poserUneFacette(ctx, doc, surface, block.nodeId, input, revisionDeRepli));
  };

  const inline = inlinePatchFor(style);
  if (inline !== undefined) {
    // LECTURE HORS de la fenêtre de mutation (#939, garde (d)) : elle vivait
    // dedans, alors que les lectures dominent la durée d'une action et
    // maintiendraient inutilement le document en mode suggestion.
    // `format.apply` n'accepte qu'une sélection ou un ref en 1.44, jamais une
    // adresse de bloc — d'où ce ref frais, pris avant la séquence.
    const ref = await freshBlockRef(ctx, block.nodeId);
    if (ref === undefined) {
      outcomes.push({ op: 'format.apply', ok: false, detail: `no provider ref available for nodeId=${block.nodeId}` });
    } else {
      await runSurface('format.apply', { ref, inline });
    }
  }
  if (style.paragraphStyleId !== undefined) await runSurface('styles.paragraph.setStyle', { target: paragraphTarget, styleId: style.paragraphStyleId });
  if (style.alignment !== undefined) await runSurface('format.paragraph.setAlignment', { target: paragraphTarget, alignment: style.alignment });
  if (style.indentation !== undefined) {
    const { leftTwips, rightTwips, firstLineTwips, hangingTwips } = style.indentation;
    await runSurface('format.paragraph.setIndentation', {
      target: paragraphTarget,
      ...(leftTwips === undefined ? {} : { left: leftTwips }),
      ...(rightTwips === undefined ? {} : { right: rightTwips }),
      // firstLine and hanging are mutually exclusive in the 1.44 validator; hanging
      // wins because OOXML w:ind gives it precedence when both are authored.
      ...(hangingTwips !== undefined
        ? { hanging: hangingTwips }
        : firstLineTwips === undefined ? {} : { firstLine: firstLineTwips }),
    });
  }
  if (style.spacing !== undefined) {
    const { beforePt, afterPt, line, lineRule } = style.spacing;
    // ⚠️ **`setSpacing` et le nœud SDM ne parlent PAS la même unité** (mesuré le
    // 2026-08-16) : `before:240` posé se relit `before:12`, donc l'écriture est
    // en twips quand la lecture est en points. Et `line:360` posé se relit
    // `1.5` : sous `lineRule:'auto'`, OOXML compte l'interligne en 240es de
    // ligne, pas en twips. Poser la valeur relue telle quelle écraserait un
    // interligne simple par un interligne de 1,5/20e de point.
    //
    // L'interligne n'est donc copié QUE sous `'auto'`, la seule règle dont la
    // conversion a été mesurée. Sous `exact` / `atLeast`, on copie l'avant et
    // l'après et on laisse l'interligne : mieux vaut une facette non copiée
    // qu'une facette copiée à l'aveugle dans la mauvaise unité.
    const lineEnAuto = lineRule === 'auto' && line !== undefined ? Math.round(line * 240) : undefined;
    await runSurface('format.paragraph.setSpacing', {
      target: paragraphTarget,
      ...(beforePt === undefined ? {} : { before: Math.round(beforePt * TWIPS_PER_POINT) }),
      ...(afterPt === undefined ? {} : { after: Math.round(afterPt * TWIPS_PER_POINT) }),
      ...(lineEnAuto === undefined ? {} : { line: lineEnAuto, lineRule: 'auto' }),
    });
  }
  if (style.numbering?.numId !== undefined) {
    await runSurface('format.paragraph.setNumbering', {
      target: paragraphTarget,
      numId: style.numbering.numId,
      ...(style.numbering.level === undefined ? {} : { level: style.numbering.level }),
    });
  }

  return outcomes;
}

function styleProviderCallsCheck(outcomes: readonly StyleStepOutcome[]): VerificationCheck {
  const failed = outcomes.filter((outcome) => !outcome.ok);
  return {
    name: 'style_provider_calls',
    passed: failed.length === 0,
    message: failed.length === 0
      ? 'Every style provider call succeeded (NO_OP replies count as already applied).'
      : `Style provider calls failed: ${failed.map((outcome) => `${outcome.op} → ${outcome.detail ?? 'unknown failure'}`).join('; ')}. Remaining facets were still attempted; the effective_style check reflects the final document state.`,
  };
}

/**
 * Ce que la vérification `effective_style` compare RÉELLEMENT : les facettes
 * que les lectures par bloc publient. Le message le dit, pour qu'un « passed »
 * ne se lise pas comme « police et taille conformes ».
 */
const MESSAGE_EFFECTIVE_STYLE_INSERTION = 'Every inserted paragraph must match the style source on the facets the per-block reads actually publish (named style, alignment, indentation, spacing, numbering, and any direct run formatting that was readable). Empty paragraphs are compared on paragraph facets only. A pass says nothing about facets listed by formatting_not_observable.';

/**
 * Déclare au reçu les facettes de police qu'AUCUNE lecture n'a publiées.
 *
 * ⚠️ `passed:true` n'est pas un succès de vérification : c'est une déclaration
 * (même forme que `style_source_document_default`). Un reçu `ok` exige que
 * toutes ses vérifications passent (`CanvasActionReceiptOkSchema`) ; le contrat
 * partagé n'a pas de champ « non vérifiable » dédié, et un `passed:false` ferait
 * rejouer une insertion déjà faite — donc dupliquer du texte. Le message porte
 * le fait, en tête, sans ambiguïté.
 */
function formattingNotObservableCheck(nonObservees: readonly FacetteDePoliceNonObservee[]): VerificationCheck[] {
  if (nonObservees.length === 0) return [];
  return [{
    name: 'formatting_not_observable',
    passed: true,
    message: `NOT VERIFIED (declaration, not a pass): ${nonObservees.join(', ')}. This SuperDoc build publishes only DIRECT run formatting; a font or size inherited from a style (the usual case for body text) is visible to no read. Do not tell the lawyer that font or size conform on the basis of this receipt — if the lawyer requires a specific font or size, say that you cannot set or verify it yourself yet, and that the lawyer can set it directly in the canvas: select the text, then use the font and size controls of the canvas toolbar.`,
  }];
}

async function styleSourceForInput(
  ctx: CanvasActionContext,
  input: Extract<StyleActionInput, { action: 'insert_paragraphs' }>,
  preBlocks: readonly CanvasSnapshotBlock[],
  rawBlocks: readonly Record<string, unknown>[],
  ancreResolueNodeId?: string,
): Promise<{ ok: true; style: EffectiveBlockStyle; target?: CanvasSnapshotBlock; verification: 'fingerprint' | 'styleId' | 'document_default' } | { ok: false; receipt: CanvasActionReceipt }> {
  if (input.styleSource?.kind === 'styleId') {
    return { ok: true, style: { paragraphStyleId: input.styleSource.styleId }, verification: 'styleId' };
  }
  const sourceSelector = input.styleSource?.kind === 'like' ? input.styleSource.selector : undefined;
  // Le refus « avant mutation » protège une PROMESSE : si l'agent a demandé un
  // style précis et qu'on ne sait pas le copier, mieux vaut échouer que
  // prétendre l'avoir appliqué. Mais quand il n'a RIEN demandé, cette même
  // rigueur devient un mur : sur un canvas généré page blanche, l'unique
  // paragraphe est vide, donc `isBodyBlock` (qui exige du texte) ne retient
  // aucun voisin — et l'insertion était refusée alors que rien n'avait été
  // promis. Incident 2026-08-03 : l'agent ouvrait le canvas et ne pouvait
  // JAMAIS y écrire la première ligne. Le texte est l'essentiel, le style
  // hérité n'est qu'un confort : on insère au style par défaut du document et
  // on le déclare dans le receipt plutôt que de bloquer.
  //
  // Ce repli est volontairement limité au cas « AUCUN voisin » — voir le refus
  // maintenu plus bas quand un voisin existe mais que son style est illisible.
  const styleSourceWasRequested = input.styleSource !== undefined;
  const source = sourceSelector === undefined
    ? await selectDefaultInsertionStyleSource(preBlocks, input.placement, async (bloc) => {
        const raw = rawBlockByNodeId(rawBlocks, bloc.nodeId);
        return raw === undefined ? undefined : (await lireStyleEffectifDuBloc(ctx, raw)).style;
      }, ancreResolueNodeId)
    : await resolveTarget(ctx, sourceSelector, input.action);
  if (source === undefined) {
    if (!styleSourceWasRequested) return { ok: true, style: {}, verification: 'document_default' };
    const pre = await snapshot(ctx);
    return { ok: false, receipt: abortedBeforeMutation({ intent: input.action, snapshot: toCanvasSnapshotSummary(pre), code: 'STYLE_SOURCE_NOT_FOUND', message: `insert_paragraphs found no block matching the requested styleSource, so it has nothing to copy formatting from (document has ${preBlocks.length} block(s)). ${SORTIE_SANS_SOURCE_DE_STYLE}`, nextAction: NEXT_ACTION_SANS_SOURCE_DE_STYLE, recovery: RECOVERY_SANS_SOURCE_DE_STYLE }) };
  }
  if ('ok' in source && !source.ok) {
    const pre = await snapshot(ctx);
    return { ok: false, receipt: abortedBeforeMutation({ intent: input.action, snapshot: toCanvasSnapshotSummary(pre), code: source.code, message: source.message }) };
  }
  const target = 'ok' in source ? source.target : source;
  const rawSource = rawBlockByNodeId(rawBlocks, target.nodeId);
  const lecture = rawSource === undefined
    ? { style: undefined, lu: false, echec: `the requested style source (nodeId=${target.nodeId}) is not in the block scan taken before mutation` }
    : await lireStyleEffectifDuBloc(ctx, rawSource);
  const style = lecture.style;
  if (style !== undefined && !hasObservableEffectiveStyle(style) && lecture.lu) {
    // ⚠️ **Une lecture ABOUTIE qui ne rapporte rien EST une réponse** (#991,
    // mesuré le 2026-08-16). C'est le fait qui manquait : le bloc a été lu, son
    // nœud ne porte ni style nommé, ni alignement, ni retrait, ni espacement —
    // il est au STYLE PAR DÉFAUT du document. « Comme ce bloc-là » se réalise
    // donc en insérant au défaut, et le refus n'y protégeait aucune promesse :
    // il n'y avait rien à copier, et rien à trahir. Ce refus s'est déclenché
    // six fois d'affilée dans la conversation autopsiée, sur des blocs
    // exactement dans cet état, pour finir par pousser l'agent à purger le
    // document de l'avocat afin de le réécrire.
    //
    // Ce que le pinceau ne sait PAS relire est déclaré au receipt plutôt que taxé
    // de zéro : les marques de run (italique, gras, police, couleur) et les
    // bordures ne sont publiées par aucune des deux surfaces en 2.6.0. Si le
    // bloc source en portait, elles ne sont pas copiées — et l'agent doit
    // l'apprendre du receipt, pas l'avocat de son écran.
    return { ok: true, style: {}, target, verification: 'document_default' };
  }
  if (style === undefined || !hasObservableEffectiveStyle(style)) {
    // ⚠️ **Le critère est la PROMESSE, pas la présence d'un voisin** (#980,
    // 2026-08-15). Ce refus a été écrit contre « le provider n'expose pas le
    // style d'un voisin qui existe », lu comme le symptôme d'une lecture
    // cassée. Sous `superdoc@2` ce n'est plus un symptôme : la projection SDM
    // ne rapporte AUCUNE facette de style, pour AUCUN bloc (#986). Le refus se
    // déclenchait donc sur tout document non vide — mesuré sur les payloads de
    // l'incident 2026-08-03, l'agent réussissait sa première insertion (pas de
    // voisin avec du texte) puis se voyait refuser toutes les suivantes. Un
    // canvas où l'agent ne peut écrire qu'une ligne est le défaut même que
    // l'incident avait fermé, revenu par la porte d'à côté.
    //
    // On applique donc ici la distinction déjà actée juste au-dessus pour
    // « aucun voisin » : si l'agent a demandé un style précis, une promesse a
    // été faite et le refus la protège ; s'il n'a rien demandé, l'héritage
    // n'était qu'un confort — on insère au style par défaut du document et on
    // le DÉCLARE dans le receipt (`style_source_document_default`), plutôt que
    // d'empêcher toute écriture.
    //
    // ⚠️ Ce qu'il reste ici après #991 est le seul cas honnête de refus : la
    // LECTURE elle-même n'a pas abouti (`lecture.lu === false`). On ne sait
    // alors pas si le bloc est nu ou richement formaté, donc on ne peut ni
    // copier, ni déclarer un défaut. Le cas « lu, et vide » a été traité plus
    // haut, en insertion réussie.
    if (!styleSourceWasRequested) return { ok: true, style: {}, verification: 'document_default' };
    const pre = await snapshot(ctx);
    return { ok: false, receipt: abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'STYLE_SOURCE_UNOBSERVABLE',
      message: `insert_paragraphs could not READ the style of the requested source block (nodeId=${target.nodeId}): ${lecture.echec ?? 'the per-block style read returned no usable answer'}. Refusing before mutation rather than claiming formatting was copied. ${SORTIE_SANS_SOURCE_DE_STYLE}`,
      nextAction: NEXT_ACTION_SANS_SOURCE_DE_STYLE,
      recovery: RECOVERY_SANS_SOURCE_DE_STYLE,
    }) };
  }
  return { ok: true, style, target, verification: 'fingerprint' };
}

/** Provider placement vocabulary, deliberately separate from the agent contract. */
type CreateParagraphPlacement =
  | { kind: 'documentStart' }
  | { kind: 'documentEnd' }
  | { kind: 'before'; target: { kind: 'block'; nodeType: string; nodeId: string } }
  | { kind: 'after'; target: { kind: 'block'; nodeType: string; nodeId: string } };

/**
 * SuperDoc returns the freshly created block identity. It is needed to preserve
 * order in a multi-paragraph insertion; its receipt is not used as proof that
 * the document actually changed.
 */
type CreatedParagraph = { kind: 'block'; nodeType: string; nodeId: string };

function createdParagraphBlock(receipt: unknown): CreatedParagraph | undefined {
  if (!isRecord(receipt) || !isRecord(receipt.paragraph)) return undefined;
  const nodeId = receipt.paragraph.nodeId;
  if (typeof nodeId !== 'string' || nodeId.trim().length === 0) return undefined;
  return {
    kind: 'block',
    nodeType: typeof receipt.paragraph.nodeType === 'string' ? receipt.paragraph.nodeType : 'paragraph',
    nodeId,
  };
}

type CreateParagraphsResult =
  | { outcome: 'complete'; provider: unknown; created: CreatedParagraph[] }
  | { outcome: 'provider_failure'; provider: unknown; created: CreatedParagraph[]; failedAt: number }
  | { outcome: 'identity_missing'; provider: unknown; created: CreatedParagraph[]; failedAt: number };

// The agent request has a finite end-to-end timeout. The insertion receipt is
// its critical path; provenance only enables the optional convenience of
// withdrawing this exact proposal later. One all-story page per phase prevents
// a heavily redlined document from turning that optional proof into dozens of
// serial provider calls. Missing provenance remains fail-closed on withdrawal.
const MAX_TRACKED_CHANGES_FOR_AGENT_INSERTION_LEDGER = 200;

/**
 * Provider identities, not positional guesses, are the only safe mutation
 * targets after a multi-paragraph creation. A stale or partial lot may leave a
 * perfectly valid old paragraph at the expected index.
 */
function rawBlocksForCreatedParagraphs(
  rawBlocks: readonly Record<string, unknown>[],
  created: readonly CreatedParagraph[],
): Record<string, unknown>[] | undefined {
  const result: Record<string, unknown>[] = [];
  for (const paragraph of created) {
    const raw = rawBlockByNodeId(rawBlocks, paragraph.nodeId);
    if (raw === undefined) return undefined;
    result.push(raw);
  }
  return result;
}

/** Proves that the provider-created identities still occupy the requested contiguous placement. */
function createdParagraphsMatchPlacement(
  rawBlocks: readonly Record<string, unknown>[],
  created: readonly CreatedParagraph[],
  placement: CanvasPlacement,
  target?: CanvasSnapshotBlock,
): boolean {
  if (created.length === 0) return false;
  const indices = created.map((paragraph) => rawBlocks.findIndex((block) => nodeIdFromBlock(block) === paragraph.nodeId));
  if (indices.some((index) => index < 0) || !indices.every((index, position) => position === 0 || index === indices[position - 1]! + 1)) return false;

  const first = indices[0]!;
  const last = indices[indices.length - 1]!;
  if (placement.at === 'document_start') return first === 0;
  if (placement.at === 'document_end') return last === rawBlocks.length - 1;
  const targetIndex = rawBlocks.findIndex((block) => nodeIdFromBlock(block) === target?.nodeId);
  if (targetIndex < 0) return false;
  return placement.at === 'before' ? last + 1 === targetIndex : first === targetIndex + 1;
}

/**
 * Creates one native paragraph per requested text. `create.paragraph` is the
 * qualified tracked primitive: structural `doc.insert` accepts tracked mode
 * while producing no revision, so it must never be used as a fallback here.
 */
async function createParagraphs(
  ctx: InsertCapableContext,
  input: Extract<StyleActionInput, { action: 'insert_paragraphs' }>,
  target: CanvasSnapshotBlock | undefined,
  initialRevision: RevisionDeDocument,
): Promise<CreateParagraphsResult> {
  const initialPlacement: CreateParagraphPlacement = input.placement.at === 'before' || input.placement.at === 'after'
    ? { kind: input.placement.at, target: { kind: 'block', nodeType: target!.nodeType, nodeId: target!.nodeId } }
    : input.placement.at === 'document_start'
      ? { kind: 'documentStart' }
      : { kind: 'documentEnd' };

  let placement = initialPlacement;
  let lastReceipt: unknown;
  const created: CreatedParagraph[] = [];
  for (const [index, text] of input.texts.entries()) {
    const expectedRevision = index === 0
      ? (ctx.expectedRevision ?? await revisionCourante(ctx, initialRevision))
      : await revisionCourante(ctx, initialRevision);
    const receipt = await muterSousFences(
      ctx,
      `insert_paragraphs:create.paragraph[${index}]`,
      { changeMode: modeDeRevision(ctx), expectedRevision },
      // `""` = paragraphe VIDE d'espacement : créé SANS `text` (sondé en suivi,
      // 2026-09-30). Beaucoup d'actes aèrent leurs paragraphes ainsi ; refuser
      // ces vides poussait l'agent à les supprimer, et l'acte ressortait collé.
      () => ctx.doc.create?.paragraph?.(text === '' ? { at: placement } : { at: placement, text }, { changeMode: modeDeRevision(ctx), expectedRevision }),
    );
    lastReceipt = receipt;
    if (isRecord(receipt) && receipt.success === false) {
      return { outcome: 'provider_failure', provider: receipt, created, failedAt: index };
    }
    const paragraph = createdParagraphBlock(receipt);
    if (paragraph === undefined) {
      return { outcome: 'identity_missing', provider: receipt, created, failedAt: index };
    }
    created.push(paragraph);
    placement = { kind: 'after', target: paragraph };
    signalerLAvancee(ctx, created.length, input.texts.length);
  }
  return { outcome: 'complete', provider: lastReceipt, created };
}

/**
 * Beaucoup de modèles d'avocat aèrent le corps par un paragraphe vide entre deux paragraphes, sans espacement de
 * style. Un lot inséré à côté d'un paragraphe ainsi encadré sortait collé, en pavé compact (mise en demeure du
 * 2026-10-28) : l'agent n'avait lu que l'ancre, pas ses voisins. Le lot reprend donc ce rythme lui-même, comme il
 * reprend déjà le style du corps voisin : chaque texte est suivi d'au moins un vide. Les `""` de l'agent restent à leur
 * place, sauf ceux de tête, que le vide déjà présent avant le lot doublerait.
 *
 * Chaque texte précède son vide : en `after`, le lot se pose après le vide qui suit l'ancre. Ainsi un refus du
 * fournisseur en cours de lot ne laisse jamais un vide seul à la place du texte demandé. Un lot qui dépasserait le
 * plafond une fois aéré est laissé tel quel.
 */
function aererLeLot(
  texts: readonly string[],
  placement: CanvasPlacement,
  rawBlocks: readonly Record<string, unknown>[],
  blocks: readonly CanvasSnapshotBlock[],
  ancre: CanvasSnapshotBlock | undefined,
): { texts: string[]; ancre: CanvasSnapshotBlock | undefined; videsAjoutes: number; videsDeTeteRetires: number } | undefined {
  if (ancre === undefined || (placement.at !== 'before' && placement.at !== 'after')) return undefined;
  const rang = rawBlocks.findIndex((block) => nodeIdFromBlock(block) === ancre.nodeId);
  const estVide = (block: Record<string, unknown> | undefined): boolean => (
    block !== undefined && block.nodeType === 'paragraph' && textFromBlock(block).trim() === ''
  );
  if (rang < 0 || textFromBlock(rawBlocks[rang]!).trim() === '' || !estVide(rawBlocks[rang - 1]) || !estVide(rawBlocks[rang + 1])) {
    return undefined;
  }
  const premierTexte = texts.findIndex((texte) => texte !== '');
  if (premierTexte < 0) return undefined;
  const aere = texts.slice(premierTexte).flatMap((texte, rangDansLeLot, lot) => (
    texte !== '' && lot[rangDansLeLot + 1] !== '' ? [texte, ''] : [texte]
  ));
  const videsDeTeteRetires = premierTexte;
  const videsAjoutes = aere.length - (texts.length - videsDeTeteRetires);
  if ((videsAjoutes === 0 && videsDeTeteRetires === 0) || aere.length > CANVAS_INSERT_PARAGRAPHS_MAX) return undefined;
  const videSuivant = nodeIdFromBlock(rawBlocks[rang + 1]!);
  const ancreDuLot = placement.at === 'after' ? blocks.find((bloc) => bloc.nodeId === videSuivant) : ancre;
  if (ancreDuLot === undefined) return undefined;
  return { texts: aere, ancre: ancreDuLot, videsAjoutes, videsDeTeteRetires };
}

export async function executeInsertParagraphs(ctx: InsertCapableContext, input: Extract<StyleActionInput, { action: 'insert_paragraphs' }>): Promise<CanvasActionReceipt> {
  // Ce verbe crée du texte brut. Les marques recopiées de la lecture devenaient des étoiles visibles
  // (incident de reprise de modèle, 2026-10-21). Refuser avant la première création, sans retirer une forme demandée.
  const avecMarques = input.texts.findIndex((texte) => Object.values(lireLesMarques(texte).marques).some((plages) => plages.length > 0));
  if (avecMarques >= 0) {
    const pre = await snapshot(ctx);
    return abortedBeforeMutation({
      intent: input.action, snapshot: toCanvasSnapshotSummary(pre), code: 'INSERT_PARAGRAPHS_REQUIRES_PLAIN_TEXT',
      recovery: { action: 'retry', reason: 'No paragraph was created. Correct the plain-text insertion as the message says and resend without re-reading.' },
      message: `insert_paragraphs refused before any write: texts[${avecMarques}] contains emphasis delimiters in ${JSON.stringify(input.texts[avecMarques]!.slice(0, 160))}. `
        + 'Insert plain text without **, * or __ emphasis, then use format_text on the inserted paragraph to apply bold, italic or underline. '
        + 'For a document heading, use insert_heading with the appropriate level instead of a plain paragraph. '
        + 'To intentionally write literal paired delimiters, use replace_text scoped to one paragraph after insertion. No paragraph was created.',
    });
  }
  const placementTarget = input.placement.at === 'before' || input.placement.at === 'after'
    ? await resolveTarget(ctx, input.placement.selector, input.action)
    : undefined;
  if (placementTarget !== undefined && !placementTarget.ok) {
    const pre = await snapshot(ctx);
    return abortedBeforeMutation({ intent: input.action, snapshot: toCanvasSnapshotSummary(pre), code: placementTarget.code, message: placementTarget.message });
  }

  const pre = await snapshot(ctx);
  const revisionsAvant = pre.trackedChanges;
  const rawPreBlocks = await scanRawBlocks(ctx);
  if (!rawPreBlocks.ok) {
    return abortedBeforeMutation({ intent: input.action, snapshot: toCanvasSnapshotSummary(pre), code: rawPreBlocks.code, message: 'insert_paragraphs could not read provider styles before mutation; refusing before mutating the document.' });
  }
  const preBlocks = toSelectionBlocks(rawPreBlocks.blocks);
  const styleSource = await styleSourceForInput(
    ctx, input, preBlocks, rawPreBlocks.blocks,
    placementTarget?.ok ? placementTarget.target.nodeId : undefined,
  );
  if (!styleSource.ok) return styleSource.receipt;
  // A copied style is a promise made to the agent. Discovering only after text
  // exists that SuperDoc cannot apply one of its facets leaves a document that
  // should not be retried but also does not meet the requested contract.
  const missingSurfaces = missingStyleSurfaces(ctx.doc, styleSource.style);
  if (missingSurfaces.length > 0) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'CAPABILITY_UNAVAILABLE',
      message: `insert_paragraphs requires ${missingSurfaces.join(', ')} to apply the promised style before any paragraph is created.`,
    });
  }
  if (typeof ctx.doc.create?.paragraph !== 'function') {
    return abortedBeforeMutation({ intent: input.action, snapshot: toCanvasSnapshotSummary(pre), code: 'CAPABILITY_UNAVAILABLE', message: 'insert_paragraphs requires editor.doc.create.paragraph to add tracked paragraphs.' });
  }
  const target = placementTarget?.ok ? placementTarget.target : undefined;
  const aeration = aererLeLot(input.texts, input.placement, rawPreBlocks.blocks, preBlocks, target);
  const lot = aeration === undefined ? input : { ...input, texts: aeration.texts };
  const ancreDuLot = aeration === undefined ? target : aeration.ancre;
  const resolvedTargets = [
    ...(target === undefined ? [] : [targetSummary(target, placementTarget?.ok ? placementTarget.candidateCount : undefined)]),
    ...(styleSource.target === undefined || styleSource.target.nodeId === target?.nodeId ? [] : [targetSummary(styleSource.target)]),
  ];
  const preSnapshot = toCanvasSnapshotSummary(pre);
  const preNodeIds = new Set(rawPreBlocks.blocks.map(nodeIdFromBlock).filter((nodeId): nodeId is string => nodeId !== undefined));
  // The insertion ledger grants a later automatic tracked-change decision.
  // Its ownership proof must span every story, but only inside the bounded
  // operational budget above. A larger review state is not "empty": it simply
  // withholds optional automatic withdrawal without delaying this insertion.
  const completeTrackedChangesBefore = ctx.agentTrackedInsertionLedger === undefined
    ? undefined
    : await readCompleteTrackedChangesAtDocumentRevision(ctx.doc, pre.revision, {
      maxTrackedChanges: MAX_TRACKED_CHANGES_FOR_AGENT_INSERTION_LEDGER,
    });
  const creation = await createParagraphs(ctx, lot, ancreDuLot, pre.revision);
  const post = await snapshot(ctx);
  const postSnapshot = toCanvasSnapshotSummary(post);

  // A provider success without a native block id means there may be a new
  // paragraph, but no later write can be tied to it. Positional recovery would
  // be unsafe: an old paragraph can occupy the expected slot after a partial lot.
  if (creation.outcome === 'identity_missing') {
    ctx.verifierEpoque('insert_paragraphs:identity-missing');
    return inconclusiveAfterMutation({
      intent: input.action,
      snapshot: preSnapshot,
      postSnapshot,
      resolvedTargets,
      code: 'CREATED_BLOCK_ID_UNAVAILABLE',
      message: `create.paragraph[${creation.failedAt}] returned success without the native nodeId required to address its result. The document may have changed; no style or page-break repair was attempted and this action must not be retried.`,
    });
  }

  // A refusal before any confirmed creation remains an ordinary provider
  // failure. A refusal after one or more known identities is handled below as
  // a partial mutation that must never be retried.
  if (creation.outcome === 'provider_failure' && creation.created.length === 0) {
    ctx.verifierEpoque('insert_paragraphs:first-create-refused');
    return buildVerifiedReceipt({
      intent: input.action,
      regimeSuivi: ctx.regimeSuivi,
      preSnapshot,
      postSnapshot,
      resolvedTargets,
      provider: creation.provider,
      revisionsAvant: idsDeRevision(revisionsAvant),
      revisionsApres: idsDeRevision(post.trackedChanges),
      verification: [{ name: 'create_paragraph', passed: false, message: 'The provider refused create.paragraph before any native paragraph identity was returned.' }],
    });
  }

  const rawPostBlocks = await scanRawBlocks(ctx);
  if (!rawPostBlocks.ok) {
    ctx.verifierEpoque('insert_paragraphs:post-create-scan-failed');
    return inconclusiveAfterMutation({
      intent: input.action,
      snapshot: preSnapshot,
      postSnapshot,
      resolvedTargets,
      code: rawPostBlocks.code,
      message: 'create.paragraph returned native identities, but the document could not be re-read to verify them. No style or page-break repair was attempted; do not retry this mutation.',
    });
  }

  const createdBlocks = rawBlocksForCreatedParagraphs(rawPostBlocks.blocks, creation.created);
  const confirmedCreatedTexts = createdBlocks !== undefined
    && createdBlocks.length === creation.created.length
    && createdBlocks.every((block, index) => textFromBlock(block) === lot.texts[index]);
  const confirmedCreatedAreNew = createdBlocks !== undefined
    && creation.created.every((paragraph) => !preNodeIds.has(paragraph.nodeId));

  if (creation.outcome === 'provider_failure') {
    if (!confirmedCreatedTexts || !confirmedCreatedAreNew || createdBlocks === undefined) {
      ctx.verifierEpoque('insert_paragraphs:partial-create-unverified');
      return inconclusiveAfterMutation({
        intent: input.action,
        snapshot: preSnapshot,
        postSnapshot,
        resolvedTargets,
        code: 'PARTIAL_CREATE_UNVERIFIABLE',
        message: `create.paragraph[${creation.failedAt}] was refused after ${creation.created.length} native identity/identities were returned, but the post-read could not prove those created paragraphs exactly. No secondary mutation was attempted; do not retry.`,
      });
    }
    ctx.verifierEpoque('insert_paragraphs:partial-create-receipt');
    return partialAfterMutation({
      intent: input.action,
      preSnapshot,
      postSnapshot,
      resolvedTargets,
      code: 'PARTIAL_PROVIDER_FAILURE',
      message: `create.paragraph[${creation.failedAt}] was refused after ${creation.created.length} of ${lot.texts.length} paragraph(s) were created and independently re-read${aeration === undefined ? '' : ` (your ${input.texts.length} entr(y/ies) were spaced to ${lot.texts.length} paragraphs copying the neighbors' empty-paragraph spacing; created before the refusal, in order: ${creation.created.slice(0, 20).map((paragraphe, rang) => `${paragraphe.nodeId}=${lot.texts[rang] === '' ? '(empty)' : JSON.stringify(lot.texts[rang]!.slice(0, 40))}`).join(', ')}${creation.created.length > 20 ? ', …' : ''})`}. The known paragraphs are in the document; no style or page-break repair was attempted, so do NOT retry this action.`,
      verification: [
        { name: 'partial_created_blocks_present', passed: true, message: 'Every provider-returned native identity from the partial lot is present after the refusal.' },
        { name: 'partial_created_texts', passed: true, message: 'Every known created paragraph has its requested text in provider-returned creation order.' },
      ],
      effects: [{
        kind: 'insert_blocks',
        reviewability: 'direct_untracked',
        description: 'Inserted only the provider-confirmed prefix of the requested paragraph lot before the next create.paragraph call was refused.',
        nodeIds: creation.created.map((paragraph) => paragraph.nodeId),
      }],
      recoveryReason: 'At least one paragraph from the requested lot is already present; retrying would duplicate it.',
    });
  }

  if (!confirmedCreatedTexts || !confirmedCreatedAreNew || createdBlocks === undefined) {
    ctx.verifierEpoque('insert_paragraphs:complete-create-unverified');
    return inconclusiveAfterMutation({
      intent: input.action,
      snapshot: preSnapshot,
      postSnapshot,
      resolvedTargets,
      code: 'CREATED_BLOCKS_UNVERIFIABLE',
      message: 'create.paragraph returned every native identity, but the post-read could not prove every created block and text. No style or page-break repair was attempted; do not retry this action.',
    });
  }

  const inserted = createdBlocks;
  const insertedNodeIds = creation.created.map((paragraph) => paragraph.nodeId);
  const createdAtRequestedPlacement = createdParagraphsMatchPlacement(
    rawPostBlocks.blocks,
    creation.created,
    lot.placement,
    ancreDuLot,
  );
  // The identities and texts are real, but a provider that ignored the requested
  // placement may have raced or misapplied the operation. Do not style or clear
  // page breaks until the newly created lot is proven to occupy that placement.
  if (!createdAtRequestedPlacement) {
    ctx.verifierEpoque('insert_paragraphs:created-at-unexpected-placement');
    return inconclusiveAfterMutation({
      intent: input.action,
      snapshot: preSnapshot,
      postSnapshot,
      resolvedTargets,
      code: 'CREATED_PARAGRAPH_STRUCTURE_CHANGED',
      message: 'The provider-created paragraphs and text were re-read, but they are not contiguous at the requested placement. No style or page-break repair was attempted; do not retry this action.',
    });
  }
  // Le style ATTENDU se juge paragraphe par paragraphe : un paragraphe vide
  // (`""`, espacement) n'a aucun run, donc ni police ni gras à recevoir ni à
  // relire — seules ses facettes de paragraphe sont promises et vérifiées.
  const styleAttendu = (index: number): EffectiveBlockStyle => (
    lot.texts[index] === '' ? facettesDeParagrapheSeules(styleSource.style) : styleSource.style
  );
  const concordeAvecLaSource = (styles: readonly EffectiveBlockStyle[]): boolean => (
    styles.length === lot.texts.length && styles.every((style, index) => (
      styleSource.verification === 'styleId'
        ? style.paragraphStyleId === styleSource.style.paragraphStyleId
        : effectiveStyleFingerprint(style) === effectiveStyleFingerprint(styleAttendu(index))
    ))
  );
  // Un paragraphe dont rien n'est attendu (vide, source nue en paragraphe) n'a
  // pas à exposer de facette : exiger l'inverse ferait échouer une insertion
  // conforme sur la seule absence de ce qu'on n'avait pas demandé.
  const tousObservables = (styles: readonly EffectiveBlockStyle[]): boolean => styles.every((style, index) => (
    !hasObservableEffectiveStyle(styleAttendu(index)) || hasObservableEffectiveStyle(style)
  ));
  // ⚠️ **Le saut de page HÉRITÉ se retire AVANT toute relecture** (#998) : c'est
  // une mutation, et la relecture de style qui suit doit voir le document final.
  // Il se retire aussi avant la réparation de style, qui pourrait reposer un
  // `styleId` — donc rouvrir un saut porté par le style nommé, là où celui-ci
  // n'est pas une propriété directe du paragraphe.
  // The provider-returned identities, not a slice at the insertion position,
  // are the exclusive targets for direct follow-up mutations. The post-read
  // above established that each is new and contains its requested text.
  const sautsRetires = await retirerLeSautHerite(ctx, ctx.doc, creation.created, post.revision);
  // La relecture des blocs insérés emprunte LA MÊME surface que la capture de
  // la source (#991) : comparer deux lectures inégales rendrait un écart
  // permanent, donc une réparation de style après chaque insertion réussie.
  const insertedStyles = (await Promise.all(inserted.map((block) => lireStyleEffectifDuBloc(ctx, block)))).map((lecture) => lecture.style);
  const insertedStylesObservable = tousObservables(insertedStyles);
  const textsMatch = inserted.length === lot.texts.length
    && inserted.every((block, index) => textFromBlock(block) === lot.texts[index])
    && createdAtRequestedPlacement;
  // `document_default` = aucun style source à répliquer, donc rien à vérifier ni
  // à réparer : toute comparaison d'empreinte déclencherait une réparation de
  // style vers une cible qui n'existe pas.
  const styleWasPromised = styleSource.verification !== 'document_default';
  const initialStyleMatched = !styleWasPromised
    ? true
    : styleSource.verification === 'styleId'
      ? concordeAvecLaSource(insertedStyles)
      : insertedStylesObservable && concordeAvecLaSource(insertedStyles);
  const repairOutcomes: StyleStepOutcome[] = [];
  if (!initialStyleMatched && insertedNodeIds.length === lot.texts.length) {
    for (const [index, block] of inserted.entries()) {
      const nodeId = nodeIdFromBlock(block);
      if (nodeId === undefined) continue;
      const nodeType = typeof block.nodeType === 'string' ? block.nodeType : 'paragraph';
      repairOutcomes.push(...await applyEffectiveStyleToBlock(ctx, ctx.doc, { nodeId, nodeType }, styleAttendu(index), post.revision));
    }
  }
  // Any direct follow-up mutation can advance the document or change the
  // provider-created structure. In particular, clearing an inherited page
  // break is not merely a visual repair: its successful acknowledgement may
  // carry a new revision. Reusing the pre-cleanup snapshot would make the
  // receipt advance the broker with stale state, so re-read after either class
  // of secondary mutation before certifying placement or revision.
  const hasSecondaryDirectMutation = repairOutcomes.length > 0 || sautsRetires.length > 0;
  const rawPostAfterStyleRepair = hasSecondaryDirectMutation ? await scanRawBlocks(ctx) : rawPostBlocks;
  const postAfterStyleRepair = hasSecondaryDirectMutation ? await snapshot(ctx) : post;
  if (!rawPostAfterStyleRepair.ok) {
    ctx.verifierEpoque('insert_paragraphs:post-style-repair-scan-failed');
    return inconclusiveAfterMutation({
      intent: input.action,
      snapshot: preSnapshot,
      postSnapshot: toCanvasSnapshotSummary(postAfterStyleRepair),
      resolvedTargets,
      code: rawPostAfterStyleRepair.code,
      message: 'The document could not be re-read after direct style repair, so the completed insertion cannot be attributed safely. Do not retry this action.',
    });
  }
  const insertedAfterStyleRepair = rawBlocksForCreatedParagraphs(rawPostAfterStyleRepair.blocks, creation.created);
  if (insertedAfterStyleRepair === undefined) {
    ctx.verifierEpoque('insert_paragraphs:post-style-repair-identities-missing');
    return inconclusiveAfterMutation({
      intent: input.action,
      snapshot: preSnapshot,
      postSnapshot: toCanvasSnapshotSummary(postAfterStyleRepair),
      resolvedTargets,
      code: 'CREATED_BLOCKS_UNVERIFIABLE_AFTER_REPAIR',
      message: 'The post-style-repair scan no longer exposes every provider-created block identity. Do not retry this action.',
    });
  }
  if (!createdParagraphsMatchPlacement(rawPostAfterStyleRepair.blocks, creation.created, lot.placement, ancreDuLot)) {
    ctx.verifierEpoque('insert_paragraphs:post-style-repair-placement-changed');
    return inconclusiveAfterMutation({
      intent: input.action,
      snapshot: preSnapshot,
      postSnapshot: toCanvasSnapshotSummary(postAfterStyleRepair),
      resolvedTargets,
      code: 'CREATED_PARAGRAPH_STRUCTURE_CHANGED',
      message: 'The provider-created paragraphs were at the requested placement before direct repair, but the final post-read no longer proves that placement. Do not retry this action.',
    });
  }
  const stylesApresReparation = repairOutcomes.length === 0
    ? insertedStyles
    : (await Promise.all(insertedAfterStyleRepair.map((block) => lireStyleEffectifDuBloc(ctx, block)))).map((lecture) => lecture.style);
  const stylesObservableAfterRepair = repairOutcomes.length === 0
    ? insertedStylesObservable
    : tousObservables(stylesApresReparation);
  const styleMatched = !styleWasPromised
    ? true
    : insertedAfterStyleRepair.length === lot.texts.length && concordeAvecLaSource(stylesApresReparation);
  // Un style nommé explicite est une promesse ENTIÈREMENT relue (`styleRef`) :
  // la police qu'il porte l'accompagne par construction. Toute autre voie
  // (copie d'un bloc, défaut du document) ne sait rien de la police que la
  // lecture n'a pas publiée — voir `FACETTES_DE_POLICE`.
  const policeNonObservee: FacetteDePoliceNonObservee[] = styleSource.verification === 'styleId'
    ? []
    : facettesDePoliceNonObservees(styleSource.verification === 'document_default' ? undefined : styleSource.style);
  // `document_default` n'a rien promis, donc rien comparé : le reçu n'a pas le
  // droit d'y écrire `formattingMatched:true` (reçus du 2026-09-30).
  const formattingMatched = styleWasPromised
    ? formattingMatchedObservable(styleMatched, policeNonObservee)
    : undefined;
  const insertedBlocksAreNew = insertedNodeIds.length === lot.texts.length
    && insertedNodeIds.every((nodeId) => !preNodeIds.has(nodeId));
  const blockDeltaMatches = postAfterStyleRepair.counts.blocks === pre.counts.blocks + lot.texts.length;
  const directStyleRepairPerformed = repairOutcomes.some((outcome) => outcome.ok && outcome.detail !== 'NO_OP (already applied)');
  const inheritedPageBreakCleared = sautsRetires.some((outcome) => outcome.ok && outcome.detail !== 'NO_OP (already applied)');
  const revisionsApres = postAfterStyleRepair.trackedChanges;
  ctx.verifierEpoque('insert_paragraphs:receipt');

  // A new tracked-change identity establishes reviewability only when its
  // navigation target names the exact provider-created block. Counting any new
  // identity would let an unrelated lawyer edit, or one tracked paragraph in a
  // larger lot, make the entire lot look reviewable.
  const idsAvant = new Set(idsDeRevision(revisionsAvant));
  const identitesNeuves = revisionsApres.filter((identite) => !idsAvant.has(identite.id));
  const blocsInseres = new Set(insertedNodeIds);
  const identitesAttribuees = identitesNeuves.filter(
    (identite) => identite.blockId !== undefined && blocsInseres.has(identite.blockId),
  );
  const idsAttribues = new Set(identitesAttribuees.map((identite) => identite.blockId!));
  const insertionIntegralementSuivie = ctx.regimeSuivi !== false
    && insertedNodeIds.length > 0
    && insertedNodeIds.every((nodeId) => idsAttribues.has(nodeId));
  const couverturePartielleSuivie = ctx.regimeSuivi !== false
    && idsAttribues.size > 0
    && !insertionIntegralementSuivie;
  const identiteNouvelleInadressable = ctx.regimeSuivi !== false
    && identitesNeuves.some((identite) => identite.blockId === undefined);

  // The created ids and exact texts were already re-read above. If the final
  // structure no longer reflects their requested placement/count, another
  // concurrent document change may have intervened: do not reduce that to a
  // retryable validation failure.
  if (!textsMatch || !insertedBlocksAreNew || !blockDeltaMatches) {
    return inconclusiveAfterMutation({
      intent: input.action,
      snapshot: preSnapshot,
      postSnapshot: toCanvasSnapshotSummary(postAfterStyleRepair),
      resolvedTargets,
      code: 'CREATED_PARAGRAPH_STRUCTURE_CHANGED',
      message: 'Every provider-created paragraph identity was initially confirmed, but the final document structure no longer proves the requested placement and block delta. Do not retry this action.',
    });
  }
  if (identiteNouvelleInadressable) {
    return inconclusiveAfterMutation({
      intent: input.action,
      snapshot: preSnapshot,
      postSnapshot: toCanvasSnapshotSummary(postAfterStyleRepair),
      resolvedTargets,
      code: 'TRACKED_REVISION_IDENTITY_UNADDRESSABLE',
      message: 'A new tracked-change identity has no navigation target blockId, so the completed paragraph lot cannot be attributed safely. Do not retry this action.',
    });
  }
  if (couverturePartielleSuivie) {
    return partialAfterMutation({
      intent: input.action,
      preSnapshot,
      postSnapshot: toCanvasSnapshotSummary(postAfterStyleRepair),
      resolvedTargets,
      code: 'TRACKED_REVISION_COVERAGE_INCOMPLETE',
      message: `The ${insertedNodeIds.length}-paragraph lot was written, but only ${idsAttribues.size} paragraph(s) have an attributed new tracked revision. Do not retry: the text is already present and the lot cannot be presented as uniformly reviewable.`,
      verification: [
        { name: 'inserted_texts_adjacent', passed: textsMatch, message: 'Post-read must show every provider-created paragraph adjacent in the requested order at the requested placement with exact text.' },
        { name: 'inserted_blocks_new', passed: insertedBlocksAreNew, message: 'Every provider-created block identity must be absent before mutation.' },
        { name: 'block_delta', passed: blockDeltaMatches, message: 'Document block count must increase by the number of requested inserted paragraphs.' },
        { name: 'tracked_revision_coverage', passed: false, message: `Only ${idsAttribues.size} of ${insertedNodeIds.length} provider-created paragraphs have an attributed new tracked revision.` },
      ],
      effects: [
        { kind: 'insert_blocks', reviewability: 'tracked', description: 'Inserted a paragraph lot with incomplete tracked-revision coverage.', nodeIds: insertedNodeIds },
        ...(directStyleRepairPerformed ? [{ kind: 'repair_inserted_style', reviewability: 'direct_untracked' as const, description: 'Applied copied style directly to provider-created blocks.', nodeIds: insertedNodeIds }] : []),
        ...(inheritedPageBreakCleared ? [{ kind: 'clear_inherited_page_break', reviewability: 'direct_untracked' as const, description: 'Cleared page-break-before directly on provider-created blocks.', nodeIds: insertedNodeIds }] : []),
      ],
      ...(formattingMatched === undefined ? {} : { formattingMatched }),
      recoveryReason: 'The requested paragraphs are already present, but their tracked-revision coverage is incomplete.',
    });
  }
  const styleOrVisibilityIncomplete = !styleMatched
    || repairOutcomes.some((outcome) => !outcome.ok)
    || sautsRetires.some((outcome) => !outcome.ok);
  if (styleOrVisibilityIncomplete) {
    return partialAfterMutation({
      intent: input.action,
      preSnapshot,
      postSnapshot: toCanvasSnapshotSummary(postAfterStyleRepair),
      resolvedTargets,
      code: 'INSERTION_FORMATTING_INCOMPLETE',
      message: 'Every requested paragraph was created and re-read, but its promised style or inherited-page-break cleanup was not fully verified. The text is already present; do not retry this action.',
      verification: [
        { name: 'inserted_texts_adjacent', passed: true, message: 'Every provider-created paragraph is present at the requested placement with exact text.' },
        { name: 'inserted_blocks_new', passed: true, message: 'Every provider-created block identity was absent before mutation.' },
        { name: 'block_delta', passed: true, message: 'Document block count increased by the requested paragraph count.' },
        { name: 'effective_style', passed: styleMatched, message: MESSAGE_EFFECTIVE_STYLE_INSERTION },
        ...formattingNotObservableCheck(policeNonObservee),
        ...(repairOutcomes.length === 0 ? [] : [styleProviderCallsCheck(repairOutcomes)]),
        ...(sautsRetires.length === 0 ? [] : [{
          name: 'inherited_page_break_cleared',
          passed: sautsRetires.every((outcome) => outcome.ok),
          message: 'The inherited page-break cleanup must succeed for every provider-created paragraph.',
        }]),
      ],
      effects: [
        insertionIntegralementSuivie
          ? { kind: 'insert_blocks', reviewability: 'tracked', description: 'Inserted every requested paragraph with tracked revisions attributed to its provider-created block.', nodeIds: insertedNodeIds }
          : { kind: 'insert_blocks', reviewability: 'direct_untracked', description: 'Inserted every requested paragraph, although complete tracked-revision coverage was not established.', nodeIds: insertedNodeIds },
        ...(directStyleRepairPerformed ? [{ kind: 'repair_inserted_style', reviewability: 'direct_untracked' as const, description: 'Applied a subset of the copied style directly before a later style step failed.', nodeIds: insertedNodeIds }] : []),
        ...(inheritedPageBreakCleared ? [{ kind: 'clear_inherited_page_break', reviewability: 'direct_untracked' as const, description: 'Cleared inherited page breaks directly on a subset of the provider-created paragraphs.', nodeIds: insertedNodeIds }] : []),
      ],
      ...(formattingMatched === undefined ? {} : { formattingMatched }),
      recoveryReason: 'The requested paragraphs are already present, but their formatting or visibility cleanup is incomplete.',
    });
  }

  const verified = buildVerifiedReceipt({
      intent: input.action,
      regimeSuivi: ctx.regimeSuivi,
      preSnapshot,
      postSnapshot: toCanvasSnapshotSummary(postAfterStyleRepair),
      resolvedTargets,
      provider: creation.provider,
      // La preuve d'insertion suivie tient sur des IDENTITÉS relues, plus sur un
      // compteur global : une frappe de l'avocat qui annule une insertion en
      // attente fait BAISSER ce compteur (#939, garde (c)).
      //
      // ⚠️ Les identités sont publiées dans les DEUX branches — y compris quand
      // le geste n'a produit aucune révision. Cesser de les publier là ferait
      // disparaître `disparuesInattendues`, donc la troisième issue de #939 (c),
      // sur le chemin devenu le plus courant sous V2 (revue #980). Ce qui change
      // entre les deux branches, c'est la POSTCONDITION exigée, pas la lecture.
      revisionsAvant: idsDeRevision(revisionsAvant),
      revisionsApres: idsDeRevision(revisionsApres),
      ...(insertionIntegralementSuivie ? {} : { mouvementAttendu: 'aucun' as const }),
      verification: [
        { name: 'inserted_texts_adjacent', passed: textsMatch, message: 'Post-read must show every inserted paragraph adjacent in the requested order with exact text.' },
        { name: 'inserted_blocks_new', passed: insertedBlocksAreNew, message: 'Every block at the insertion position must be new (absent before mutation) — a pre-existing block there means the insert did not happen or duplicated content.' },
        { name: 'block_delta', passed: blockDeltaMatches, message: 'Document block count must increase by the number of requested inserted paragraphs.' },
        ...(aeration === undefined ? [] : [{
          name: 'spacing_paragraphs_matched',
          passed: true,
          message: `The placement anchor (nodeId=${target?.nodeId ?? 'unknown'}) sits between two empty paragraphs: this document separates its body paragraphs with an empty paragraph. To follow that spacing, ${aeration.videsAjoutes} empty paragraph(s) were added after text(s) you did not follow with one${aeration.videsDeTeteRetires === 0 ? '' : `, and your ${aeration.videsDeTeteRetires} leading empty entr(y/ies) were dropped (the existing empty paragraph already precedes the lot)`}. Written: ${lot.texts.length} paragraph(s), every text followed by at least one empty paragraph. Do not add empty paragraphs around them.`,
        }]),
        { name: 'style_observable', passed: !styleWasPromised || styleSource.verification === 'styleId' || stylesObservableAfterRepair, message: 'Copied style verification requires observable effective style fields on every inserted block after the direct style repair.' },
        { name: 'effective_style', passed: styleMatched, message: MESSAGE_EFFECTIVE_STYLE_INSERTION },
        ...formattingNotObservableCheck(policeNonObservee),
        // Rendu explicite dans le receipt : l'agent doit savoir que les
        // paragraphes portent le style par défaut du document, pas un style
        // hérité — c'est ce qui lui permet de décider s'il veut un apply_style.
        // Deux chemins mènent au défaut du document, et l'agent doit savoir
        // LEQUEL : « je n'ai trouvé aucun voisin » et « j'ai lu le voisin, il
        // est nu » n'appellent pas la même suite. Le second dit aussi ce que la
        // copie ne couvre pas : en 2.6.0, aucune lecture par bloc ne publie les
        // marques de run ni les bordures, donc un italique du voisin n'aurait
        // pas été copié quand bien même il en portait un (#991).
        ...(styleWasPromised ? [] : [{
          name: 'style_source_document_default',
          passed: true,
          message: styleSource.target === undefined
            ? 'No neighboring styled block existed (blank canvas): paragraphs were inserted with the document default style. Use apply_style if a specific style is required.'
            : `The style source block (nodeId=${styleSource.target.nodeId}) was read successfully and carries no named style and no paragraph formatting of its own — it is at the document default, so the inserted paragraphs match it by taking that same default. Note that run marks (italic, bold, font, size, color) and paragraph borders are published by NO per-block read in this SuperDoc build: if the source carried any, they were not copied. Use apply_style with an explicit styleId if a specific named style is required.`,
        }]),
        // Lorsque l'agent n'a demandé aucun patron précis, il doit savoir quel
        // bloc de corps a été retenu. Une ancre de placement qui est un titre ne
        // doit jamais devenir silencieusement la source de style (#1024).
        ...(styleWasPromised && input.styleSource === undefined ? [{
          name: 'style_source_inherited',
          passed: true,
          message: `No styleSource was requested, so formatting was copied from the nearest BODY paragraph before the insertion point (or after it when none precedes): nodeId=${styleSource.target?.nodeId ?? 'unknown'} (nodeType=${styleSource.target?.nodeType ?? 'unknown'}, styleId=${styleSource.style.paragraphStyleId ?? 'none'}). Headings, blank paragraphs, centered paragraphs, title styles and bold+underlined title lines are never used as the default source.${styleSource.target !== undefined && target !== undefined && styleSource.target.nodeId !== target.nodeId ? ` The placement anchor (nodeId=${target.nodeId}, nodeType=${target.nodeType}) only fixed the POSITION; its formatting was not copied.` : ''} Pass styleSource explicitly to override this default.`,
        }] : []),
        // Le geste n'a produit aucune révision suivie : l'effet doit alors être
        // prouvé explicitement, sinon le receipt reposerait sur les seules
        // postconditions de nœud sans jamais dire que le document a bougé.
        ...(insertionIntegralementSuivie ? [] : [{
          name: 'document_revision_moved',
          passed: postAfterStyleRepair.revision !== pre.revision,
          message: 'The document revision read before and after the action must differ — the untracked insertion is proven by the document moving, not by a tracked-change identity.',
        }]),
        ...(repairOutcomes.length === 0 ? [] : [styleProviderCallsCheck(repairOutcomes)]),
        // ⚠️ Ce contrôle porte sur l'ACCUSÉ du fournisseur, faute de relecture
        // possible : `pageBreakBefore` sort toujours `false` de la projection
        // SDM (#986), donc une postcondition relue serait verte même sans
        // retrait. Le juge du retrait est la pagination peinte, hors d'ici.
        ...(sautsRetires.length === 0 ? [] : [{
          name: 'inherited_page_break_cleared',
          passed: sautsRetires.every((outcome) => outcome.ok),
          message: sautsRetires.every((outcome) => outcome.ok)
            ? 'Inserted paragraphs had the page break they inherit from their neighbour cleared (#998): a paragraph that opens its own page at the end of the document is not painted at all, so the lawyer would see a blank page instead of the text. Use insert_page_break afterwards if a break is actually wanted.'
            : `Clearing the inherited page break failed on ${sautsRetires.filter((outcome) => !outcome.ok).length} of ${sautsRetires.length} inserted paragraph(s): ${sautsRetires.filter((outcome) => !outcome.ok).map((outcome) => `${outcome.op} → ${outcome.detail ?? 'unknown failure'}`).join('; ')}. The text IS in the document, but a paragraph that inherited a page break from its neighbour (PLAISE, BORDEREAU, PIÈCES JOINTES all carry one) may not be painted on screen at all — tell the lawyer to reopen the document if a passage looks missing, and do NOT rewrite it, which would duplicate it.`,
        }]),
      ],
      effects: [
        insertionIntegralementSuivie
          ? { kind: 'insert_blocks', reviewability: 'tracked' as const, description: 'Inserted paragraphs with one re-read tracked revision attributed to every provider-created block.', nodeIds: insertedNodeIds }
          : { kind: 'insert_blocks', reviewability: 'direct_untracked' as const, description: 'Inserted paragraphs whose complete tracked-revision coverage could not be established from the post-read.', nodeIds: insertedNodeIds },
        ...(directStyleRepairPerformed ? [{ kind: 'repair_inserted_style', reviewability: 'direct_untracked' as const, description: 'Applied copied style directly to provider-created blocks after SuperDoc did not preserve it during paragraph creation.', nodeIds: insertedNodeIds }] : []),
        ...(inheritedPageBreakCleared ? [{ kind: 'clear_inherited_page_break', reviewability: 'direct_untracked' as const, description: 'Cleared page-break-before directly on provider-created paragraphs to keep the newly inserted text visible.', nodeIds: insertedNodeIds }] : []),
      ],
    });
  // Do not use the author field here: SuperDoc v2 deliberately signs the
  // agent's review revisions as the connected lawyer. The only safe ownership
  // proof is causal and local: this action created the native node, then a
  // complete all-story re-read found exactly one qualified open paragraph-
  // insertion revision whose provider navigation target points back to it.
  // A partial or malformed provider discovery is intentionally fail-closed for
  // the ledger only: the independently verified creation receipt remains valid.
  const completeTrackedChangesAfter = verified.status === 'ok'
    && completeTrackedChangesBefore?.ok === true
    ? await readCompleteTrackedChangesAtDocumentRevision(ctx.doc, postAfterStyleRepair.revision, {
      maxTrackedChanges: MAX_TRACKED_CHANGES_FOR_AGENT_INSERTION_LEDGER,
    })
    : undefined;
  const withdrawableTrackedInsertions: AgentTrackedInsertion[] = completeTrackedChangesBefore?.ok === true
    && completeTrackedChangesAfter?.ok === true
    ? (() => {
        const completeIdsBefore = new Set(idsDeRevision(completeTrackedChangesBefore.trackedChanges));
        const completeNewIdentities = completeTrackedChangesAfter.trackedChanges.filter(
          (identity) => !completeIdsBefore.has(identity.id),
        );
        return insertedNodeIds.flatMap((nodeId) => {
          const candidates = completeNewIdentities.filter((identity) =>
            isOpenParagraphInsertionRevision(identity, nodeId));
          return candidates.length === 1
            ? [{ nodeId, changeId: candidates[0]!.id }]
            : [];
        });
      })()
    : [];
  if (verified.status === 'ok' && withdrawableTrackedInsertions.length === insertedNodeIds.length) {
    ctx.agentTrackedInsertionLedger?.record({
      preRevision: pre.revision,
      postRevision: postAfterStyleRepair.revision,
      entries: withdrawableTrackedInsertions,
    });
  }
  const hasDirectSecondaryMutation = directStyleRepairPerformed || inheritedPageBreakCleared;
  return {
    ...verified,
    // A failed or inconclusive receipt must never be re-labelled as tracked by
    // the summary layer. Only a fully verified action can be tracked or mixed.
    reviewability: verified.status !== 'ok'
      ? verified.reviewability
      : insertionIntegralementSuivie
        ? hasDirectSecondaryMutation ? 'mixed' : 'tracked'
        : 'direct_untracked',
    ...(formattingMatched === undefined ? {} : { formattingMatched }),
  };
}

/**
 * Pose un style nommé sur un bloc existant (`apply_style`). Donner à des
 * paragraphes la forme d'un autre paragraphe est `copy_format`
 * (`copyFormatExecutor.ts`).
 */
export async function executeApplyStyle(ctx: CanvasActionContext, input: Extract<StyleActionInput, { action: 'apply_style' }>): Promise<CanvasActionReceipt> {
  const pre = await snapshot(ctx);
  const resolved = await resolveTarget(ctx, input.selector, input.action);
  if (!resolved.ok) {
    return abortedBeforeMutation({ intent: input.action, snapshot: toCanvasSnapshotSummary(pre), code: resolved.code, message: resolved.message });
  }
  const style: EffectiveBlockStyle = { paragraphStyleId: input.styleId };
  const doc = ctx.doc as CanvasActionContext['doc'] & StyleSurfaces;
  const missing = missingStyleSurfaces(doc, style);
  if (missing.length > 0) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'CAPABILITY_UNAVAILABLE',
      message: `apply_style requires SuperDoc Document API surfaces missing on this editor: ${missing.join(', ')}. The captured style facets cannot be applied without them.`,
    });
  }
  const outcomes = await applyEffectiveStyleToBlock(ctx, doc, { nodeId: resolved.target.nodeId, nodeType: resolved.target.nodeType }, style, pre.revision);
  const post = await snapshot(ctx);
  const rawPostBlocks = await scanRawBlocks(ctx);
  const rawTarget = rawPostBlocks.ok ? rawBlockByNodeId(rawPostBlocks.blocks, resolved.target.nodeId) : undefined;
  // Seul le style nommé est jugé : le bloc relu porte aussi ce que ce verbe n'écrit pas — la numérotation d'un style
  // qui numérote, un alignement posé à la main (`mesure-socle-styles-nommes`, a) —, qui ne dit rien du style posé.
  const concordanceObservee = rawTarget !== undefined
    && (await lireStyleEffectifDuBloc(ctx, rawTarget)).style.paragraphStyleId === input.styleId;
  // L'identifiant du style se relit toujours : aucune facette n'est « non observée », le verdict est vrai ou faux.
  const formattingMatched = formattingMatchedObservable(concordanceObservee, []);
  ctx.verifierEpoque('apply_style:receipt');
  return {
    ...buildVerifiedReceipt({
      intent: input.action,
      // ⚠️ `apply_style` écrit en DIRECT quel que soit le régime du document —
      // exception NOMMÉE, et c'est désormais un choix, plus une contrainte.
      //
      // Sa justification d'origine (« setStyle ne pose aucune révision,
      // mesuré ») est PÉRIMÉE : relevé le 2026-08-16,
      // `styles.paragraph.setStyle` s'annonce `tracked:true` et produit bien une
      // révision sous fenêtre `suggesting`. Ce qui la maintient, c'est le
      // service rendu à l'avocat : une révision de mise en forme isolée, posée
      // sur des paragraphes eux-mêmes non révisables (`doc.insert` ne produit
      // aucune révision), ne lui donne rien à décider — juste une ligne de plus
      // à accepter. Sa relisibilité reste établie par la relecture, donc le
      // receipt reste honnête si ce choix change.
      regimeSuivi: false,
      preSnapshot: toCanvasSnapshotSummary(pre),
      postSnapshot: toCanvasSnapshotSummary(post),
      resolvedTargets: [targetSummary(resolved.target, resolved.candidateCount)],
      provider: { steps: outcomes },
      // `apply_style` écrit en DIRECT : il ne pose aucune révision suivie, donc
      // sa preuve reste la relecture du style effectif. Aucune identité à
      // comparer ici — et surtout aucun compteur global à interroger.
      verification: [
        styleProviderCallsCheck(outcomes),
        { name: 'effective_style', passed: concordanceObservee, message: 'Target block must expose the requested named style on the per-block read.' },
      ],
      effects: [{ kind: 'apply_style', reviewability: 'direct_untracked', description: `Applied the named style ${input.styleId} directly to an existing block.`, nodeIds: [resolved.target.nodeId] }],
    }),
    reviewability: 'direct_untracked',
    ...(formattingMatched === undefined ? {} : { formattingMatched }),
  };
}
